import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { getParametres } from '@/api';

export interface SiteSettings {
  nomSite: string;
  logo: string | null;
  devise: string;
  telephone: string;
  email: string;
  ville: string;
  adresse: string;
  horaires: string;
  instagramUrl: string | null;
  facebookUrl: string | null;
  twitterUrl: string | null;
}

const DEFAULT_SETTINGS: SiteSettings = {
  nomSite: 'Nimba Afromarket',
  logo: null,
  devise: 'GNF',
  telephone: '',
  email: '',
  ville: '',
  adresse: '',
  horaires: '',
  instagramUrl: null,
  facebookUrl: null,
  twitterUrl: null,
};

function mapSettings(raw: any): SiteSettings {
  return {
    nomSite: raw.nom_site || DEFAULT_SETTINGS.nomSite,
    logo: raw.logo || null,
    devise: raw.devise || DEFAULT_SETTINGS.devise,
    telephone: raw.telephone || '',
    email: raw.email || '',
    ville: raw.ville || '',
    adresse: raw.adresse || '',
    horaires: raw.horaires || '',
    instagramUrl: raw.instagram_url || null,
    facebookUrl: raw.facebook_url || null,
    twitterUrl: raw.twitter_url || null,
  };
}

export function splitBrandName(nomSite: string): [string, string] {
  const idx = nomSite.indexOf(' ');
  if (idx === -1) return [nomSite.toUpperCase(), ''];
  return [nomSite.slice(0, idx).toUpperCase(), nomSite.slice(idx + 1).toUpperCase()];
}

interface SiteSettingsContextValue {
  settings: SiteSettings;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

const SiteSettingsContext = createContext<SiteSettingsContextValue | undefined>(undefined);

export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getParametres();
      setSettings(mapSettings(data));
    } catch {
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // SEO/branding dynamiques : le titre, les meta tags et le favicon suivent le nom/logo
  // configures cote admin (parametres_site), au lieu de rester figes sur des valeurs statiques.
  useEffect(() => {
    const { nomSite, logo } = settings;
    const tagline = "L'Excellence Guineenne a portee de main";
    const description = `Decouvrez l'artisanat guineen sublime sur ${nomSite}. Mode, beaute, culture et artisanat local livres a votre porte. Fait main, fait local, fait avec amour.`;

    document.title = `${nomSite} - ${tagline}`;

    const setMeta = (id: string, content: string) => {
      document.getElementById(id)?.setAttribute('content', content);
    };
    setMeta('meta-description', description);
    setMeta('meta-og-title', `${nomSite} - ${tagline}`);
    setMeta('meta-og-description', description);
    setMeta('meta-twitter-title', nomSite);
    setMeta('meta-twitter-description', tagline);

    const favicon = document.getElementById('favicon');
    if (logo) {
      favicon?.setAttribute('href', logo);
    } else {
      const letter = nomSite.trim().charAt(0).toUpperCase();
      if (letter) {
        const svg = `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><circle cx='50' cy='50' r='46' fill='%23FDFBF7' stroke='%23D4AF37' stroke-width='6'/><text x='50' y='51' font-family='Georgia,serif' font-size='48' font-weight='bold' fill='%23D4AF37' text-anchor='middle' dominant-baseline='central'>${letter}</text></svg>`;
        favicon?.setAttribute('href', svg);
      } else {
        favicon?.removeAttribute('href');
      }
    }

    const ldJson = document.getElementById('ld-json');
    if (ldJson) {
      ldJson.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: nomSite,
        url: '/',
        description,
      });
    }
  }, [settings]);

  return (
    <SiteSettingsContext.Provider value={{ settings, isLoading, refetch: fetchSettings }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  const ctx = useContext(SiteSettingsContext);
  if (!ctx) {
    throw new Error('useSiteSettings must be used within a SiteSettingsProvider');
  }
  return ctx;
}
